import React from 'react';
import { useGetPatientMedProfiles } from '../../../pages/MedicationProfile/hook';
import type { MedProfile } from '../../../pages/MedicationProfile/hook/types';

interface MedicationProfilesContextType {
  medProfiles: MedProfile[];
  isLoading: boolean;
  refresh: () => Promise<void>;
}

const MedicationProfilesContext =
  React.createContext<MedicationProfilesContextType | null>(null);

export const useMedicationProfilesShared =
  (): MedicationProfilesContextType => {
    const ctx = React.useContext(MedicationProfilesContext);
    if (!ctx) {
      throw new Error(
        'useMedicationProfilesShared must be used within MedicationProfilesProvider',
      );
    }
    return ctx;
  };

const MedicationProfilesProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { postGetPatientMedProfiles, medProfiles, isLoading } =
    useGetPatientMedProfiles();

  const refresh = React.useCallback(async () => {
    await postGetPatientMedProfiles();
  }, [postGetPatientMedProfiles]);

  React.useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = React.useMemo<MedicationProfilesContextType>(
    () => ({
      medProfiles: Array.isArray(medProfiles) ? medProfiles : [],
      isLoading: !!isLoading,
      refresh,
    }),
    [medProfiles, isLoading, refresh],
  );

  return (
    <MedicationProfilesContext.Provider value={value}>
      {children}
    </MedicationProfilesContext.Provider>
  );
};

export default MedicationProfilesProvider;
